import {
  AppBar,
  TextField,
  Button,
  InputAdornment,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  Box, 
  LinearProgress,
  Chip
} from "@mui/material";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  LabelList,
} from 'recharts';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchProjectUtilisation, fetchProjectUtilisationDateChange, setEmployeeUtilisation } from "../../../actions/candidates_actions";
import moment from "moment";

const colors = [
  "#2196F3", "#4CAF50", "#FF9800", "#F44336", "#9C27B0", 
  "#9966cc", "#b08599", "#c59f7d", "#d7b96e", "#e7d16d",
  "#c8e658", "#9fcc43", "#74b335", "#5da621", "#46990d",
  "#39b000", "#289900", "#188200", "#086b00", "#005500",
  "#009966", "#00b080", "#00c599", "#00d7b2", "#00e7cc",
  "#00c8e6", "#009fcc", "#0074b3", "#005da6", "#004699",
  "#0039b0", "#002899", "#001882", "#00086b", "#000055",
  "#3300cc", "#5200b0", "#6b0099", "#800080", "#990066",
  "#b00052", "#c50039", "#d70028", "#e70018", "#ff0008",
  "#cc0000", "#b00000", "#990000", "#820000", "#6b0000",
  "#550000", "#663300", "#805200", "#996b00", "#b28000"
]

const CustomIconLabel = (props) => {
  const { x, y, width, value } = props;
  const iconSize = 25;
  const verticalGap = 4;
  if (value <= 100) {
    return null; 
  }
  return (
    <g>
      <svg
        x={x + width / 2 - iconSize / 2}
        y={y - iconSize - verticalGap}
        width={iconSize}
        height={iconSize}
        viewBox="0 0 25 25"
        fill="none"
        stroke="none"
      >
        <path
          d="M10 3h4v12h-4z" // Draws a thick vertical rectangle (body of !)
          fill="#ff8c81"
        />
        <circle
          cx="12" // Center X
          cy="19" // Center Y (adjust for dot positioning)
          r="2"  // Radius of the dot
          fill="#ff8c81"
        />
      </svg>
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // payload[0] refers to the first (and often only) data item when hovering over a single bar.
    // If you have multiple bars (e.g., stacked bars), you'll need to iterate through payload.
    const data = payload[0].payload; // This is your original data item for the hovered bar
    // console.log(payload)
    const _data = payload.map((item) => {
      if(item.value > 0){
        return (
          <p className="data" style={{color:item.fill}}>
          {`${item.dataKey}: ${item.value}%`}
          </p>
        )
      }
      else{
        return <></>
      }
    })

    return (
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        padding: '10px',
        borderRadius: '5px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}>
        <p className="label">{`${data.datelabel}`}</p>
        <p className="intro">{`Total Utilisation: ${data.totalUtilisation}%`}</p>
        {_data}
        {data.totalUtilisation > 100 && (
          <Paper style={{backgroundColor: '#ff8c81', marginBlock: '20px'}}>
            <Grid container spacing={2} direction="row" justifyContent="space-between" alignItems="center" style={{width: '100%', paddingInline: '20px', justifyContent: 'space-between'}}>
                <Grid item size>
                    <Typography variant="body2">
                        Utilisation exceeds 100%!
                    </Typography>
                </Grid>
            </Grid>
          </Paper>
        )}
      </div>
    );
  }

  return null;
};

export const Utilisation = () => {
  const dispatch = useDispatch();
  const { 
    weeklyWorkingHours = 40, 
    coreRoleUtilisationPercentage = 100, 
    projects = [], 
    projectTimelineGraph = [] 
  } = useSelector((state) => state.candidates.selectedCandidateData);

  const [loaded, setLoaded] = useState(false);
  const [newWeeklyWorkingHours, setNewWeeklyWorkingHours] = useState(weeklyWorkingHours);
  const [newCoreRoleUtilisation, setNewCoreRoleUtilisation] = useState(coreRoleUtilisationPercentage);
  const [hasChanged, setHasChanged] = useState(false);
  const [date, setDate] = useState(moment());

  useEffect(() => {
    async function init() {
      if(!loaded){
        setLoaded(true)
        dispatch(fetchProjectUtilisation())
      }
    }
    init();
  }, [loaded, dispatch]);

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item size>
        <AppBar
          position="static"
          color="default"
          elevation={0}
          sx={{ mt: 5, mb: 2.5 }}
        >
          <Toolbar variant="dense" sx={{ textAlign: "center", display: "block", pt: 1.75, border: "1px solid #ddd" }}>
            <Typography variant="h6" color="inherit">
              Utilisation Dashboard
            </Typography>
          </Toolbar>
        </AppBar>
      </Grid>
      <Grid item size>
        <Paper sx={{ p: 2 }}>
          <Grid item size>
            <Typography variant="subtitle1" color="inherit">
              Employee Utilisation
            </Typography>
          </Grid>
          <Grid item size>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <Grid item size={{xs:6}}>
                <Typography variant="subtitle1" color="inherit">
                  Avg. Hours / Week
                </Typography>
                <TextField
                  type="number"
                  value={newWeeklyWorkingHours}
                  onChange={(event) => {
                    if(event.target.value >= 0){
                      setNewWeeklyWorkingHours(event.target.value);
                      if(!hasChanged){
                        setHasChanged(true)
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item size={{xs:6}}>
                <Typography variant="subtitle1" color="inherit">
                  Min Utilisation for Core Role
                </Typography>
                <Typography variant="subtitle2" color="inherit">
                  <TextField
                    type="number"
                    value={newCoreRoleUtilisation}
                    onChange={(event) => {
                      if(event.target.value >= 0){
                        setNewCoreRoleUtilisation(event.target.value);
                        if(!hasChanged){
                          setHasChanged(true)
                        }
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          %
                        </InputAdornment>
                      ),
                    }}
                  />
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs style={{ display:"flex", flexDirection:"row-reverse", width: "100%"}}>
          <Button
              variant="contained"
              color="#e0e0e0"
              disabled={!hasChanged}
              onClick={async() => {
                await dispatch(setEmployeeUtilisation(newWeeklyWorkingHours, newCoreRoleUtilisation))
                await dispatch(fetchProjectUtilisation())
                setHasChanged(false)
              }}
            >
              Update Utilisation
            </Button>
          </Grid>
        </Paper>
      </Grid>
      <Grid item size>
        <Paper sx={{ p: 2 }}>
          <Grid item size>
            <Typography variant="subtitle1" color="inherit">
              Project Utilisation
            </Typography>
          </Grid>
          <Grid item size>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Utilisation</TableCell>
                  <TableCell>Avg. Hours / Week</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project, index) => (
                  <TableRow key={index}>
                    <TableCell>{project.title}</TableCell>
                    <TableCell>{project.coreRole ? "Permanent" : project.startDate ? moment(project.startDate).format("DD/MM/YYYY") : "Unknown"}</TableCell>                    
                    <TableCell>{project.coreRole ? "Permanent" : project.endDate ? moment(project.endDate).format("DD/MM/YYYY") : "Indefinite"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 200 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={project.percentage} 
                            style={{ height: 20, borderRadius: 5 }}
                          />
                        </Box>
                        <Chip 
                          label={`${project.percentage}%`} 
                          size="small" 
                          style={{ height: 20, borderRadius: 5}}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{project.allocatedHours}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
        </Paper>
      </Grid>
      <Grid item size>
        <Paper sx={{ p: 2 }}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item size>
              <Typography variant="subtitle1" color="inherit">
                {"Project Utilisation Timeline"}
              </Typography>
            </Grid>
            <Grid item>
                <DatePicker
                    views={['year']}
                    label="Year"
                    value={date}
                    onChange={(value) => {
                        setDate(value)
                        dispatch(fetchProjectUtilisationDateChange(projects, value));
                    }}
                    renderInput={(params) => <TextField {...params} margin="normal" size="small" />}
                />
            
            </Grid>
          </Grid>
          <Grid item style={{width: '100%', height: 400}}>
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBarChart
                data={projectTimelineGraph}
                margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  tick={{ fontSize: 10 }}
                  interval={3}
                />
                <YAxis 
                  domain={[0, 120]}
                  label={{ value: 'Utilisation (%)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />}/>
                <Legend />
                <ReferenceLine y={100} stroke="red" strokeDasharray="3 3" label="Over-Utilisation" />
                {projects.map((project, index) => (
                  <Bar 
                    key={project.title}
                    dataKey={project.title} 
                    stackId="a" 
                    fill={colors[index % colors.length]} 
                    fillOpacity={0.7}
                  >
                    {index === projects.length - 1 && (
                      <LabelList dataKey="totalUtilisation" content={<CustomIconLabel />} />
                    )}
                  </Bar>
                ))}
              </RechartsBarChart>
            </ResponsiveContainer>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};
